import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Plus, BookOpen, FileText, Trash2, Loader2, FileEdit } from "lucide-react";

const MyProjects = () => {
  const navigate = useNavigate();
  const [covers, setCovers] = useState([]);
  const [listings, setListings] = useState([]);
  const [frontMatter, setFrontMatter] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      api.get("/covers"),
      api.get("/listings"),
      api.get("/frontmatter").catch(() => ({ data: [] })),
    ])
      .then(([c, l, fm]) => {
        if (!mounted) return;
        setCovers(c.data || []);
        setListings(l.data || []);
        setFrontMatter(fm.data || []);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const deleteCover = async (id) => {
    if (!confirm("Delete this cover project?")) return;
    await api.delete(`/cover/${id}`);
    setCovers((c) => c.filter((x) => x.id !== id));
  };
  const deleteListing = async (id) => {
    if (!confirm("Delete this listing?")) return;
    await api.delete(`/listings/${id}`);
    setListings((c) => c.filter((x) => x.id !== id));
  };
  const deleteFrontMatter = async (id) => {
    if (!confirm("Delete this front matter project?")) return;
    await api.delete(`/frontmatter/${id}`);
    setFrontMatter((c) => c.filter((x) => x.id !== id));
  };

  if (loading) {
    return (
      <div className="mt-12 flex items-center justify-center text-sm text-[#5F5E5A] gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading projects…
      </div>
    );
  }

  return (
    <div className="mt-8 space-y-10" data-testid="my-projects">
      {/* Cover projects */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="text-xl font-medium" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
              Cover projects
            </h2>
            <p className="text-sm text-[#5F5E5A]">{covers.length} saved</p>
          </div>
          <button
            onClick={() => navigate("/cover/new")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium hover:bg-[#3C3489] transition-colors"
            data-testid="new-cover-from-projects"
          >
            <Plus className="w-4 h-4" /> New cover
          </button>
        </div>
        {covers.length === 0 ? (
          <div className="bg-[#F1EFE8]/50 border border-dashed border-[#D3D1C7] rounded-[12px] p-10 text-center">
            <BookOpen className="w-7 h-7 text-[#534AB7] mx-auto" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-[#5F5E5A]">You haven't created any cover projects yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {covers.map((c) => (
              <div key={c.id} className="bg-white border border-[#D3D1C7] rounded-[12px] overflow-hidden group flex flex-col" data-testid={`cover-card-${c.id}`}>
                <button
                  onClick={() => navigate(`/cover/${c.id}`)}
                  className="block w-full aspect-[2/3] bg-[#F1EFE8] flex items-center justify-center overflow-hidden flex-shrink-0"
                >
                  {c.thumbnail_url ? (
                    <img src={c.thumbnail_url} alt={c.book_title} className="w-full h-full object-cover object-center" />
                  ) : (
                    <BookOpen className="w-10 h-10 text-[#534AB7]/30" strokeWidth={1.2} />
                  )}
                </button>
                <div className="p-3 flex items-center justify-between gap-2 flex-1">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate text-[#2C2C2A] leading-tight">{c.book_title || "Untitled book"}</div>
                    <div className="text-xs text-[#5F5E5A] mt-0.5">{c.trim_size || ""}{c.page_count ? ` · ${c.page_count}p` : ""}</div>
                  </div>
                  <button
                    onClick={() => deleteCover(c.id)}
                    aria-label="Delete"
                    className="p-1.5 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A] hover:text-[#BA7517] transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Front matter projects */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="text-xl font-medium" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
              Front matter
            </h2>
            <p className="text-sm text-[#5F5E5A]">{frontMatter.length} saved</p>
          </div>
          <button
            onClick={() => navigate("/frontmatter")}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] border border-[#D3D1C7] text-sm font-medium hover:bg-[#F1EFE8] transition-colors"
            data-testid="new-frontmatter-from-projects"
          >
            <Plus className="w-4 h-4" /> New front matter
          </button>
        </div>
        {frontMatter.length === 0 ? (
          <div className="bg-[#F1EFE8]/50 border border-dashed border-[#D3D1C7] rounded-[12px] p-10 text-center">
            <FileEdit className="w-7 h-7 text-[#534AB7] mx-auto" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-[#5F5E5A]">No interior front matter saved yet.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {frontMatter.map((fm) => (
              <div
                key={fm.id}
                className="bg-white border border-[#D3D1C7] rounded-[12px] p-5 flex flex-col gap-3"
                data-testid={`frontmatter-card-${fm.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <button
                    onClick={() => navigate(`/frontmatter/${fm.id}`)}
                    className="text-left min-w-0 flex-1"
                  >
                    <div className="text-sm font-medium text-[#2C2C2A] truncate">
                      {fm.book_title || "Untitled book"}
                    </div>
                    {fm.author && (
                      <div className="text-xs text-[#5F5E5A] truncate">by {fm.author}</div>
                    )}
                  </button>
                  <button
                    onClick={() => deleteFrontMatter(fm.id)}
                    aria-label="Delete"
                    className="p-1.5 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A] hover:text-[#BA7517] transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="px-2 py-0.5 rounded-md bg-[#F1EFE8] border border-[#D3D1C7] text-[#5F5E5A]">
                    {fm.trim_size}
                  </span>
                  {fm.status === "final" ? (
                    <span className="px-2 py-0.5 rounded-md bg-[#E8F6F0] border border-[#1D9E75]/30 text-[#1D9E75] font-medium">
                      Final · ISBN
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded-md bg-[#FFF4E5] border border-[#E4D6BA] text-[#7A5C0E] font-medium">
                      Draft · ISBN pending
                    </span>
                  )}
                </div>
                <button
                  onClick={() => navigate(`/book/${fm.id}`)}
                  data-testid={`book-overview-link-${fm.id}`}
                  className="mt-1 text-[11px] text-[#534AB7] hover:underline self-start"
                >
                  View book overview →
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Listings */}
      <section>
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="text-xl font-medium" style={{ fontFamily: "Outfit, Inter, sans-serif" }}>
              Saved listings
            </h2>
            <p className="text-sm text-[#5F5E5A]">{listings.length} saved</p>
          </div>
        </div>
        {listings.length === 0 ? (
          <div className="bg-[#F1EFE8]/50 border border-dashed border-[#D3D1C7] rounded-[12px] p-10 text-center">
            <FileText className="w-7 h-7 text-[#534AB7] mx-auto" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-[#5F5E5A]">No listings saved yet. Generate one and click "Save to library".</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {listings.map((l) => (
              <div key={l.id} className="bg-white border border-[#D3D1C7] rounded-[12px] p-5" data-testid={`listing-card-${l.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-xs font-semibold truncate text-[#2C2C2A] leading-tight">{l.book_title}</div>
                    <div className="text-xs text-[#5F5E5A] mt-0.5">{l.genre}</div>
                  </div>
                  <button
                    onClick={() => deleteListing(l.id)}
                    className="p-1.5 rounded-md hover:bg-[#F1EFE8] text-[#5F5E5A] hover:text-[#BA7517] transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(Array.isArray(l.keywords) ? l.keywords : []).slice(0, 4).map((k) => (
                    <span key={k} className="px-2 py-0.5 rounded-md bg-[#F1EFE8] border border-[#D3D1C7] text-[11px] text-[#2C2C2A]">
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default MyProjects;
